import { Vec3, vec3, add, sub, scale, lengthSq } from '../math/vec3'
import { Mat3, ZERO3, IDENTITY3, addMat3, scaleMat3, mulMat3, transpose, diagonal, outer } from '../math/mat3'
import { quatToMat3 } from '../math/quat'
import { Shape, PartDef } from './part'
import { Vessel, PartInstance } from './vessel'
import { PartCatalog, partDef } from './catalog'

export interface MassProperties {
  /** massa totale, kg */
  readonly mass: number
  /** centro di massa nel sistema locale del veicolo */
  readonly com: Vec3
  /** tensore d'inerzia ATTORNO AL CENTRO DI MASSA, sistema del veicolo */
  readonly inertia: Mat3
}

/**
 * Tensore d'inerzia di una forma omogenea attorno al proprio baricentro,
 * negli assi locali della parte (+Y = asse del razzo).
 */
export function shapeInertia(shape: Shape, mass: number): Mat3 {
  switch (shape.kind) {
    case 'cylinder': {
      const { radius: r, height: h } = shape
      const axial = (mass * r * r) / 2
      const perp = (mass * (3 * r * r + h * h)) / 12
      return diagonal(perp, axial, perp)
    }
    case 'cone': {
      const { radius: r, height: h } = shape
      const axial = (3 * mass * r * r) / 10
      const perp = mass * ((3 * r * r) / 20 + (3 * h * h) / 80)
      return diagonal(perp, axial, perp)
    }
    case 'box': {
      const { x, y, z } = shape
      return diagonal(
        (mass * (y * y + z * z)) / 12,
        (mass * (x * x + z * z)) / 12,
        (mass * (x * x + y * y)) / 12,
      )
    }
  }
}

export function partMass(part: PartInstance, def: PartDef, emptyTanks = false): number {
  return def.dryMass + (emptyTanks ? 0 : part.propellant)
}

/**
 * Massa, centro di massa e tensore d'inerzia del veicolo assemblato.
 *
 * Per ogni parte:
 *   I_veicolo += R · I_locale · Rᵀ  +  m (|d|² E − d ⊗ d)      con d = r_parte − com
 *
 * Il secondo termine e Huygens-Steiner: e la ragione per cui spostare una
 * parte cambia il comportamento del razzo anche a massa invariata.
 */
export function computeMassProperties(
  vessel: Vessel,
  catalog: PartCatalog,
  opts: { emptyTanks?: boolean } = {},
): MassProperties {
  if (vessel.parts.length === 0) {
    throw new Error('Veicolo senza parti: proprieta di massa non definite')
  }
  const empty = opts.emptyTanks ?? false

  let mass = 0
  let weighted = vec3(0, 0, 0)
  for (const p of vessel.parts) {
    const m = partMass(p, partDef(catalog, p.defId), empty)
    mass += m
    weighted = add(weighted, scale(p.position, m))
  }
  if (mass <= 0) throw new Error('Veicolo di massa nulla')
  const com = scale(weighted, 1 / mass)

  let inertia: Mat3 = ZERO3
  for (const p of vessel.parts) {
    const def = partDef(catalog, p.defId)
    const m = partMass(p, def, empty)
    if (m <= 0) continue

    // inerzia propria, ruotata nel sistema del veicolo
    const R = quatToMat3(p.rotation)
    const local = shapeInertia(def.shape, m)
    inertia = addMat3(inertia, mulMat3(mulMat3(R, local), transpose(R)))

    // termine di trasporto
    const d = sub(p.position, com)
    const steiner = addMat3(scaleMat3(IDENTITY3, lengthSq(d)), scaleMat3(outer(d, d), -1))
    inertia = addMat3(inertia, scaleMat3(steiner, m))
  }

  return { mass, com, inertia }
}
